import { useMemo } from 'react'

import { type ColumnDef } from '@tanstack/react-table'
import { format, isValid } from 'date-fns'
import { useUnit } from 'effector-react'

import IconDone from '@/assets/icons/icon-done-all.svg'
import IconEdit from '@/assets/icons/icon-edit.svg'
import IconFlag from '@/assets/icons/icon-flag.svg'
import IconListAdd from '@/assets/icons/icon-list-add.svg'
import IconMore from '@/assets/icons/icon-menu-more.svg'
import IconReplay from '@/assets/icons/icon-replay.svg'
import IconDelete from '@/assets/icons/icon-trash.svg'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { $account } from '@/models/account.ts'
import type { NavigationEntry } from '@/modules/tasks/components/task-navigation'
import { parseTaskDate } from '@/modules/tasks/service/task-service'
import { classNames } from '@/utils/helpers'
import { isToday } from '@/utils/is-today'

interface DefineColumnOptions {
  showType?: boolean
  showAssignedTo?: boolean
  onToggleCompleted: (task: NavigationEntry, state: boolean) => void
  onOpenDelete: (task: NavigationEntry) => void
  onOpenEdit: (task: NavigationEntry) => void
}

const defineColumns = (options: DefineColumnOptions) => {
  const {
    showAssignedTo,
    showType,
    onToggleCompleted,
    onOpenDelete,
    onOpenEdit,
  } = options
  const columns: ColumnDef<NavigationEntry>[] = [
    {
      id: 'title',
      accessorKey: 'name',
      header: 'Title',
      cell: ({ row }) => (
        <div className="flex items-center gap-[16px]">
          {row.original.type === 'task' && (
            <input
              type="checkbox"
              checked={row.original.completed}
              onChange={(event) =>
                onToggleCompleted(row.original, event.target.checked)
              }
              className="accent-primary-500"
            />
          )}
          <span className="truncate">{row.original.name}</span>
        </div>
      ),
    },
    {
      id: 'note-full',
      accessorKey: 'detail',
      header: 'Note',
    },
  ]

  if (showType) {
    columns.push({
      id: 'type',
      accessorKey: 'type',
      header: 'Type',
      cell: function Cell({ row }) {
        const account = useUnit($account)
        const { original } = row

        return useMemo(() => {
          if (original.type === 'repeatable') {
            return (
              <span className="flex gap-2 items-center">
                <IconReplay className="w-4 h-4" />
                Recurring
              </span>
            )
          }
          const date = parseTaskDate(original.dueDate)

          if (original.completed) {
            return (
              <span className="flex gap-2 items-center">
                <IconDone className="w-4 h-4" />
                Completed
              </span>
            )
          }
          if (date && (isToday(date) || date?.getTime() < Date.now())) {
            return <span>Due Today</span>
          }
          if (original.assignees) {
            const assignedToSelf = original.assignees.filter(
              (it) => it.id === account?.userId,
            )

            if (assignedToSelf) {
              return (
                <span className="flex gap-2 items-center">
                  <IconListAdd className="w-4 h-4" />
                  Assigned
                </span>
              )
            }
          }

          return (
            <span className="flex gap-2 items-center">
              <IconFlag className="w-4 h-4" />
              Planned
            </span>
          )
        }, [account?.userId, original])
      },
    })
  }
  columns.push({
    id: 'dueDate',
    accessorKey: 'dueDate',
    header: 'Due Date',
    cell: ({ row }) => {
      const end =
        row.original.type === 'task'
          ? row.original.dueDate
          : row.original.endDate
      const date = parseTaskDate(end)
      const isCompleted = row.original.type === 'task' && row.original.completed
      const isOverdue = !isCompleted && date && date?.getTime() < Date.now()
      const content =
        date && isValid(date) ? format(date, 'LLL dd, y, HH:mm') : 'No due date'

      return (
        <span
          className={classNames('truncate', {
            'text-danger-default': isOverdue,
            'text-title': !isOverdue,
          })}
        >
          {content}
        </span>
      )
    },
  })
  if (showAssignedTo) {
    columns.push({
      id: 'assigned',
      accessorKey: 'assignees',
      header: 'Assigned To',
      cell: function Cell({ row }) {
        const assignedTo = useMemo(
          () => row.original.assignees?.map((it) => it.name).join(', ') ?? '-',
          [row.original.assignees],
        )

        return <span className="line-clamp-2 truncate">{assignedTo}</span>
      },
    })
  }
  columns.push({
    id: 'actions',
    cell: ({ row }) => (
      <div className="float-right">
        <Popover>
          <PopoverTrigger>
            <IconMore className="text-title w-5 h-5 hover:text-secondary-300" />
          </PopoverTrigger>
          <PopoverContent className="w-[120px]">
            <div className="flex flex-col gap-2">
              <div
                className="flex items-center gap-2 text-title hover:text-primary-500 cursor-pointer transition duration-300 ease-out"
                onClick={() => onOpenEdit(row.original)}
              >
                <IconEdit className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Edit</span>
              </div>
              <div
                className="flex items-center gap-2 cursor-pointer text-title hover:text-danger-default transition duration-300 ease-out"
                onClick={() => onOpenDelete(row.original)}
              >
                <IconDelete className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Delete</span>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    ),
  })

  return columns
}

export interface TaskTableProps {
  tasks: NavigationEntry[]
  onToggleCompleted: (task: NavigationEntry, state: boolean) => void
  onOpenEdit: (task: NavigationEntry) => void
  onOpenDelete: (task: NavigationEntry) => void
  showType?: boolean
  showAssignedTo?: boolean
}

export const TaskTable = (props: TaskTableProps) => {
  const {
    tasks,
    showAssignedTo,
    showType,
    onToggleCompleted,
    onOpenDelete,
    onOpenEdit,
  } = props

  const columns = useMemo(
    () =>
      defineColumns({
        showType,
        showAssignedTo,
        onToggleCompleted,
        onOpenDelete,
        onOpenEdit,
      }),
    [onOpenDelete, onOpenEdit, onToggleCompleted, showAssignedTo, showType],
  )

  return (
    <DataTable
      columns={columns}
      data={tasks}
    />
  )
}
